import { useEffect } from "react";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";
import { getLanguageFromPath } from "@/hooks/useLanguageFromUrl";

interface MetaContent {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogImageAlt: string;
}

const BASE_URL = "https://blog-esg.com";

// Use the original Portuguese OG image as the universal image
// AI image generation has difficulty with accurate text rendering
// The original image works well for all languages as the visual design is clear
const UNIVERSAL_OG_IMAGE = `${BASE_URL}/og-image-custom.png`;

// Dynamic OG images per page and language - using universal image
const ogImages: Record<Language, Record<string, { url: string; alt: string }>> = {
  pt: {
    home: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Sustentabilidade Para Todos - Consultoria ESG",
    },
    blog: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Blog ESG - Perguntas e Respostas sobre Sustentabilidade",
    },
    auth: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Painel Administrativo",
    },
    admin: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Painel Administrativo",
    },
  },
  en: {
    home: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Sustainability For Everyone - ESG Consulting",
    },
    blog: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "ESG Blog - Q&A about Sustainability",
    },
    auth: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Admin Panel",
    },
    admin: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Admin Panel",
    },
  },
  es: {
    home: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Sostenibilidad Para Todos - Consultoría ESG",
    },
    blog: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Blog ESG - Preguntas y Respuestas sobre Sostenibilidad",
    },
    auth: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Panel Administrativo",
    },
    admin: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Panel Administrativo",
    },
  },
  fr: {
    home: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Durabilité Pour Tous - Conseil ESG",
    },
    blog: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Blog ESG - Questions-Réponses sur la Durabilité",
    },
    auth: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Panneau d'Administration",
    },
    admin: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - Panneau d'Administration",
    },
  },
  zh: {
    home: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - 人人可持续 - ESG咨询",
    },
    blog: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "ESG博客 - 可持续发展问答",
    },
    auth: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - 管理面板",
    },
    admin: {
      url: UNIVERSAL_OG_IMAGE,
      alt: "Domani Global - 管理面板",
    },
  },
};

const metaTranslations: Record<Language, Record<string, MetaContent>> = {
  pt: {
    home: {
      title: "Domani Global - Consultoria ESG e Sustentabilidade Empresarial",
      description: "Soluções completas em ESG, inventário de emissões, créditos de carbono e neutralização. Transforme sua empresa em referência de sustentabilidade.",
      ogTitle: "Domani Global - Consultoria ESG",
      ogDescription: "Soluções completas em ESG, créditos de carbono e sustentabilidade empresarial.",
      ogImage: ogImages.pt.home.url,
      ogImageAlt: ogImages.pt.home.alt,
    },
    blog: {
      title: "Blog ESG - Perguntas e Respostas sobre Sustentabilidade | Domani",
      description: "Tire suas dúvidas sobre ESG, créditos de carbono, mudanças climáticas e sustentabilidade empresarial. Respostas de especialistas.",
      ogTitle: "Blog ESG - Domani Global",
      ogDescription: "Perguntas e respostas sobre sustentabilidade, ESG e créditos de carbono.",
      ogImage: ogImages.pt.blog.url,
      ogImageAlt: ogImages.pt.blog.alt,
    },
    cases: {
      title: "Casos de Sucesso ESG - TransJordano | Domani Global",
      description: "Conheça o caso de sucesso da TransJordano: 27+ anos de tradição, 8 anos sem acidentes graves, certificações ISO e reconhecimento Top of Mind. Relatório ESG completo.",
      ogTitle: "Casos de Sucesso ESG - TransJordano",
      ogDescription: "Case TransJordano: 27+ anos de excelência em logística sustentável com certificações internacionais.",
      ogImage: UNIVERSAL_OG_IMAGE,
      ogImageAlt: "TransJordano - Caso de Sucesso ESG - Domani Global",
    },
    auth: {
      title: "Login - Painel Administrativo | Domani Global",
      description: "Acesse o painel administrativo da Domani Global para gerenciar conteúdos e configurações.",
      ogTitle: "Login - Domani Global",
      ogDescription: "Acesse o painel administrativo da Domani Global.",
      ogImage: ogImages.pt.auth.url,
      ogImageAlt: ogImages.pt.auth.alt,
    },
    admin: {
      title: "Painel Administrativo | Domani Global",
      description: "Gerencie perguntas, respostas e configurações do site Domani Global.",
      ogTitle: "Admin - Domani Global",
      ogDescription: "Painel administrativo Domani Global.",
      ogImage: ogImages.pt.admin.url,
      ogImageAlt: ogImages.pt.admin.alt,
    },
  },
  en: {
    home: {
      title: "Domani Global - ESG Consulting & Corporate Sustainability",
      description: "Complete solutions in ESG, emissions inventory, carbon credits and neutralization. Transform your company into a sustainability reference.",
      ogTitle: "Domani Global - ESG Consulting",
      ogDescription: "Complete solutions in ESG, carbon credits and corporate sustainability.",
      ogImage: ogImages.en.home.url,
      ogImageAlt: ogImages.en.home.alt,
    },
    blog: {
      title: "ESG Blog - Q&A about Sustainability | Domani",
      description: "Get answers about ESG, carbon credits, climate change and corporate sustainability. Expert answers.",
      ogTitle: "ESG Blog - Domani Global",
      ogDescription: "Questions and answers about sustainability, ESG and carbon credits.",
      ogImage: ogImages.en.blog.url,
      ogImageAlt: ogImages.en.blog.alt,
    },
    cases: {
      title: "ESG Success Cases - TransJordano | Domani Global",
      description: "Discover TransJordano's success story: 27+ years of tradition, 8 years without major accidents, ISO certifications and Top of Mind recognition. Complete ESG Report.",
      ogTitle: "ESG Success Cases - TransJordano",
      ogDescription: "TransJordano case: 27+ years of excellence in sustainable logistics with international certifications.",
      ogImage: UNIVERSAL_OG_IMAGE,
      ogImageAlt: "TransJordano - ESG Success Case - Domani Global",
    },
    auth: {
      title: "Login - Admin Panel | Domani Global",
      description: "Access the Domani Global admin panel to manage content and settings.",
      ogTitle: "Login - Domani Global",
      ogDescription: "Access the Domani Global admin panel.",
      ogImage: ogImages.en.auth.url,
      ogImageAlt: ogImages.en.auth.alt,
    },
    admin: {
      title: "Admin Panel | Domani Global",
      description: "Manage questions, answers and site settings for Domani Global.",
      ogTitle: "Admin - Domani Global",
      ogDescription: "Domani Global admin panel.",
      ogImage: ogImages.en.admin.url,
      ogImageAlt: ogImages.en.admin.alt,
    },
  },
  es: {
    home: {
      title: "Domani Global - Consultoría ESG y Sostenibilidad Empresarial",
      description: "Soluciones completas en ESG, inventario de emisiones, créditos de carbono y neutralización. Transforma tu empresa en referencia de sostenibilidad.",
      ogTitle: "Domani Global - Consultoría ESG",
      ogDescription: "Soluciones completas en ESG, créditos de carbono y sostenibilidad empresarial.",
      ogImage: ogImages.es.home.url,
      ogImageAlt: ogImages.es.home.alt,
    },
    blog: {
      title: "Blog ESG - Preguntas y Respuestas sobre Sostenibilidad | Domani",
      description: "Resuelve tus dudas sobre ESG, créditos de carbono, cambio climático y sostenibilidad empresarial. Respuestas de expertos.",
      ogTitle: "Blog ESG - Domani Global",
      ogDescription: "Preguntas y respuestas sobre sostenibilidad, ESG y créditos de carbono.",
      ogImage: ogImages.es.blog.url,
      ogImageAlt: ogImages.es.blog.alt,
    },
    cases: {
      title: "Casos de Éxito ESG - TransJordano | Domani Global",
      description: "Conoce el caso de éxito de TransJordano: 27+ años de tradición, 8 años sin accidentes graves, certificaciones ISO y reconocimiento Top of Mind. Informe ESG completo.",
      ogTitle: "Casos de Éxito ESG - TransJordano",
      ogDescription: "Case TransJordano: 27+ años de excelencia en logística sostenible con certificaciones internacionales.",
      ogImage: UNIVERSAL_OG_IMAGE,
      ogImageAlt: "TransJordano - Caso de Éxito ESG - Domani Global",
    },
    auth: {
      title: "Iniciar Sesión - Panel Administrativo | Domani Global",
      description: "Accede al panel administrativo de Domani Global para gestionar contenidos y configuraciones.",
      ogTitle: "Login - Domani Global",
      ogDescription: "Accede al panel administrativo de Domani Global.",
      ogImage: ogImages.es.auth.url,
      ogImageAlt: ogImages.es.auth.alt,
    },
    admin: {
      title: "Panel Administrativo | Domani Global",
      description: "Gestiona preguntas, respuestas y configuraciones del sitio Domani Global.",
      ogTitle: "Admin - Domani Global",
      ogDescription: "Panel administrativo Domani Global.",
      ogImage: ogImages.es.admin.url,
      ogImageAlt: ogImages.es.admin.alt,
    },
  },
  fr: {
    home: {
      title: "Domani Global - Conseil ESG et Durabilité des Entreprises",
      description: "Solutions complètes en ESG, inventaire des émissions, crédits carbone et neutralisation. Transformez votre entreprise en référence de durabilité.",
      ogTitle: "Domani Global - Conseil ESG",
      ogDescription: "Solutions complètes en ESG, crédits carbone et durabilité des entreprises.",
      ogImage: ogImages.fr.home.url,
      ogImageAlt: ogImages.fr.home.alt,
    },
    blog: {
      title: "Blog ESG - Questions-Réponses sur la Durabilité | Domani",
      description: "Obtenez des réponses sur l'ESG, les crédits carbone, le changement climatique et la durabilité des entreprises. Réponses d'experts.",
      ogTitle: "Blog ESG - Domani Global",
      ogDescription: "Questions et réponses sur la durabilité, l'ESG et les crédits carbone.",
      ogImage: ogImages.fr.blog.url,
      ogImageAlt: ogImages.fr.blog.alt,
    },
    cases: {
      title: "Cas de Succès ESG - TransJordano | Domani Global",
      description: "Découvrez le cas de succès de TransJordano: 27+ ans de tradition, 8 ans sans accidents graves, certifications ISO et reconnaissance Top of Mind. Rapport ESG complet.",
      ogTitle: "Cas de Succès ESG - TransJordano",
      ogDescription: "Case TransJordano: 27+ ans d'excellence en logistique durable avec certifications internationales.",
      ogImage: UNIVERSAL_OG_IMAGE,
      ogImageAlt: "TransJordano - Cas de Succès ESG - Domani Global",
    },
    auth: {
      title: "Connexion - Panneau d'Administration | Domani Global",
      description: "Accédez au panneau d'administration de Domani Global pour gérer le contenu et les paramètres.",
      ogTitle: "Connexion - Domani Global",
      ogDescription: "Accédez au panneau d'administration de Domani Global.",
      ogImage: ogImages.fr.auth.url,
      ogImageAlt: ogImages.fr.auth.alt,
    },
    admin: {
      title: "Panneau d'Administration | Domani Global",
      description: "Gérez les questions, réponses et paramètres du site Domani Global.",
      ogTitle: "Admin - Domani Global",
      ogDescription: "Panneau d'administration Domani Global.",
      ogImage: ogImages.fr.admin.url,
      ogImageAlt: ogImages.fr.admin.alt,
    },
  },
  zh: {
    home: {
      title: "Domani Global - ESG咨询与企业可持续发展",
      description: "ESG、排放清单、碳信用和碳中和的完整解决方案。将您的企业打造成可持续发展的标杆。",
      ogTitle: "Domani Global - ESG咨询",
      ogDescription: "ESG、碳信用和企业可持续发展的完整解决方案。",
      ogImage: ogImages.zh.home.url,
      ogImageAlt: ogImages.zh.home.alt,
    },
    blog: {
      title: "ESG博客 - 可持续发展问答 | Domani",
      description: "获取关于ESG、碳信用、气候变化和企业可持续发展的专家解答。",
      ogTitle: "ESG博客 - Domani Global",
      ogDescription: "关于可持续发展、ESG和碳信用的问答。",
      ogImage: ogImages.zh.blog.url,
      ogImageAlt: ogImages.zh.blog.alt,
    },
    cases: {
      title: "ESG成功案例 - TransJordano | Domani Global",
      description: "了解TransJordano的成功故事：27+年传统，8年无重大事故，ISO认证和Top of Mind认可。完整ESG报告。",
      ogTitle: "ESG成功案例 - TransJordano",
      ogDescription: "TransJordano案例：27+年可持续物流卓越，拥有国际认证。",
      ogImage: UNIVERSAL_OG_IMAGE,
      ogImageAlt: "TransJordano - ESG成功案例 - Domani Global",
    },
    auth: {
      title: "登录 - 管理面板 | Domani Global",
      description: "访问Domani Global管理面板以管理内容和设置。",
      ogTitle: "登录 - Domani Global",
      ogDescription: "访问Domani Global管理面板。",
      ogImage: ogImages.zh.auth.url,
      ogImageAlt: ogImages.zh.auth.alt,
    },
    admin: {
      title: "管理面板 | Domani Global",
      description: "管理Domani Global网站的问题、答案和设置。",
      ogTitle: "管理 - Domani Global",
      ogDescription: "Domani Global管理面板。",
      ogImage: ogImages.zh.admin.url,
      ogImageAlt: ogImages.zh.admin.alt,
    },
  },
};

const getPageKey = (pathname: string): string => {
  const path = pathname.replace(/^\/(pt|en|es|fr|zh)/, "").replace(/^\//, "") || "home";
  
  if (path === "" || path === "/") return "home";
  if (path.startsWith("blog")) return "blog";
  if (path.startsWith("cases")) return "cases";
  if (path.startsWith("auth")) return "auth";
  if (path.startsWith("admin")) return "admin";
  
  return "home";
};

const getCanonicalUrl = (pathname: string, lang: Language): string => {
  const pathWithoutLang = pathname.replace(/^\/(pt|en|es|fr|zh)/, "").replace(/^\/+/, "");
  
  if (lang === "pt") {
    return pathWithoutLang ? `${BASE_URL}/${pathWithoutLang}` : BASE_URL;
  }
  
  return pathWithoutLang ? `${BASE_URL}/${lang}/${pathWithoutLang}` : `${BASE_URL}/${lang}`;
};

const getAlternateUrls = (pathname: string): Record<Language | "x-default", string> => {
  const pathWithoutLang = pathname.replace(/^\/(pt|en|es|fr|zh)/, "").replace(/^\/+/, "");
  const basePath = pathWithoutLang ? `/${pathWithoutLang}` : "";
  
  return {
    pt: `${BASE_URL}${basePath}`,
    en: `${BASE_URL}/en${basePath}`,
    es: `${BASE_URL}/es${basePath}`,
    fr: `${BASE_URL}/fr${basePath}`,
    zh: `${BASE_URL}/zh${basePath}`,
    "x-default": `${BASE_URL}${basePath}`,
  };
};

const getOgLocale = (lang: Language): string => {
  const localeMap: Record<Language, string> = {
    pt: "pt_BR",
    en: "en_US",
    es: "es_ES",
    fr: "fr_FR",
    zh: "zh_CN",
  };
  return localeMap[lang];
};

export const useDocumentMeta = () => {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const urlLanguage = getLanguageFromPath(location.pathname);
    const currentLang = urlLanguage || language;
    const pageKey = getPageKey(location.pathname);
    
    const meta = metaTranslations[currentLang]?.[pageKey] || metaTranslations.pt.home;

    // Update document title
    document.title = meta.title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", meta.description);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", meta.ogTitle);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute("content", meta.ogDescription);
    }

    // Update og:url
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", getCanonicalUrl(location.pathname, currentLang));
    }

    // Update og:image dynamically
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute("content", meta.ogImage);
    }

    // Update og:image:alt dynamically
    let ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
    if (!ogImageAlt) {
      ogImageAlt = document.createElement("meta");
      ogImageAlt.setAttribute("property", "og:image:alt");
      document.head.appendChild(ogImageAlt);
    }
    ogImageAlt.setAttribute("content", meta.ogImageAlt);

    // Update og:locale dynamically
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.setAttribute("content", getOgLocale(currentLang));
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", meta.ogTitle);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute("content", meta.ogDescription);
    }

    // Update twitter:image dynamically
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute("content", meta.ogImage);
    }

    // Update twitter:image:alt dynamically
    let twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
    if (!twitterImageAlt) {
      twitterImageAlt = document.createElement("meta");
      twitterImageAlt.setAttribute("name", "twitter:image:alt");
      document.head.appendChild(twitterImageAlt);
    }
    twitterImageAlt.setAttribute("content", meta.ogImageAlt);

    // Update twitter:url dynamically
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute("content", getCanonicalUrl(location.pathname, currentLang));
    }

    // Update html lang attribute
    document.documentElement.lang = currentLang === "pt" ? "pt-BR" : currentLang;

    // Update canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = getCanonicalUrl(location.pathname, currentLang);

    // Update hreflang tags
    const alternateUrls = getAlternateUrls(location.pathname);
    const hreflangLangs = ["pt-BR", "en", "es", "fr", "zh", "x-default"] as const;
    const langMap: Record<typeof hreflangLangs[number], keyof typeof alternateUrls> = {
      "pt-BR": "pt",
      "en": "en",
      "es": "es",
      "fr": "fr",
      "zh": "zh",
      "x-default": "x-default",
    };

    hreflangLangs.forEach((hreflang) => {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = alternateUrls[langMap[hreflang]];
    });

  }, [language, location.pathname]);
};

export default useDocumentMeta;
